import React, {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {createTable} from '@tanstack/react-table'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {UpdateListType} from '../../../component/type'
import {Button, List as ListView, Popup} from '../../../component'
import ListRight from '../../../component/listRight'
import {
  clearTestimonialDetail,
  deleteTestimonial,
  getTestimonalList,
  getTestimonialDetail,
} from '../../../store/actions'
import {useSelector, useAppDispatch} from '../../../store'
import {TestimonialType} from '../../../store/actions/types'
import {UpdateButtonList, RemoveButtonList} from '../../../component/List/Commands'
import {defaultPageSize} from '../../../configs/paginationSetting'

const table = createTable().setRowType<TestimonialType>()

const TestimonialsList = memo(() => {
  const {t} = useTranslation()
  const navigation = useNavigate()
  const {list, total, from, playerPositionList} = useSelector(({testimonial, appSetting}) => ({
    list: testimonial.testimonialList,
    total: testimonial.total,
    from: testimonial.from,
    playerPositionList: appSetting.playerPositionList,
  }))
  const [selectedRow, setSelectedRow] = useState<TestimonialType>()
  const [isOpenPopup, setIsOpenPopup] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(0)
  const [loading, setLoading] = useState(false)
  const defaultOptions = useMemo(() => ({page: 1, per_page: defaultPageSize}), [])
  const [options, setOptions] = useState(defaultOptions)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getTestimonalList(options))
  }, [options])

  const columns = useMemo(
    () => [
      table.createDataColumn('id', {
        header: 'NUMBER',
        size: 120,
        cell: ({row}) => <span>{String(row.index + from).padStart(2, '0')}</span>,
      }),
      table.createDataColumn('medias_model', {
        size: 250,
        header: 'IMAGE',
        cell: ({row}) => (
          <div className="min-w-150">
            {row.original?.medias_model &&
              row.original.medias_model?.length > 0 &&
              row.original.medias_model[0]?.original_url && (
                <img
                  src={row.original.medias_model[0].original_url}
                  alt=""
                  className="img-testimonials-list mr-2"
                />
              )}
            <span>{row.original?.name}</span>
          </div>
        ),
      }),
      table.createDataColumn('position', {
        header: 'POSITION',
        size: 200,
        cell: ({row}) => <span>{playerPositionList[row.original?.position || 0]}</span>,
      }),
      table.createDataColumn('description', {
        header: 'BODY TEXT',
        size: 800,
        cell: ({row}) => <span>{row.original?.description}</span>,
      }),
      table.createDataColumn('id', {
        header: '',
        cell: ({row}) =>
          row.original && (
            <ListRight
              row={row.original}
              onClick={onUpdatePress}
              buttonList={[UpdateButtonList, RemoveButtonList]}
            />
          ),
      }),
    ],
    [from],
  )

  const onUpdatePress = useCallback(
    async ({id, command}: UpdateListType) => {
      if (command === 'update') {
        const response = await dispatch(getTestimonialDetail(id))
        if (response) navigation(`/content-management/testimonial/${id}`)
      } else if (command === 'remove') {
        setCurrentItemId(id)
        setIsOpenPopup(true)
      } else if (command === 'deleteConfirmed') {
        setLoading(true)
        await dispatch(deleteTestimonial(id, options))
        setIsOpenPopup(false)
        setLoading(false)
      }
      setSelectedRow(undefined)
    },
    [selectedRow, list],
  )

  const onPaginate = useCallback(
    (page: number) => {
      setOptions({...options, page})
    },
    [options],
  )

  const onLimit = useCallback(
    (per_page: number) => {
      setOptions({...options, per_page, page: 1})
    },
    [options],
  )

  const onAddNewPress = useCallback(() => {
    dispatch(clearTestimonialDetail())
    navigation(`/content-management/testimonial/add`)
  }, [])

  return (
    <div className="table-outer">
      <div className="table-header">
        <div className="c-carousal__header">
          <div className="c-carousal__header__lpart v-center">
            <span className="f-Exo-Bold title-color">{t('testimonials').toUpperCase()}</span>
          </div>
          <div className="c-carousal__header__rpart">
            <Button
              icon="c-icon--plus"
              title={t('addNew')}
              classAddon="c-button--icons f-Exo-Medium"
              onClick={onAddNewPress}
            />
            <Popup
              isOpen={isOpenPopup}
              setIsOpen={setIsOpenPopup}
              onPressDelete={() => onUpdatePress({id: currentItemId, command: 'deleteConfirmed'})}
              title={t('delete')}
              description={t('deleteText')}
              loading={loading}
            />
          </div>
        </div>
      </div>
      <ListView
        columns={columns}
        data={list}
        total={total}
        onPaginate={onPaginate}
        forcePageIndex={options.page - 1}
        options={options}
        isPageSize
        onLimit={onLimit}
        pageSize={options?.per_page}
      />
    </div>
  )
})

export default TestimonialsList
